import type { ServicesApiAllDeliveriesMetric, ServicesApiChannelType } from '@/services/api/services-api';

export type ChannelOptionValue =
  | 'all'
  | 'email'
  | 'push'
  | 'in_app'
  | 'sms'
  | 'webhook'
  | 'slack'
  | 'line'
  | 'whatsapp';

interface ChannelOption {
  value: ChannelOptionValue;
  label: string;
  apiTypes: ServicesApiChannelType[] | null; // null = all types (no filter)
  supportsDelivered: boolean;
  supportsOpens: boolean;
}

/**
 * Maps user-facing channel options to their underlying API channel types.
 * Some options consolidate multiple API types (e.g. "Push" includes both
 * `push` and `urban_airship`). Only channels with metric data are shown
 * in the dropdown.
 *
 * `supportsOpens` indicates whether the channel tracks opens/clicks — SMS,
 * Webhook, and Slack do not, so the Opened series and Open-to-click rate
 * spline should be hidden when those channels are selected.
 */
export const ALL_CHANNELS_OPTION: ChannelOption = {
  value: 'all',
  label: 'All channels',
  apiTypes: null,
  supportsDelivered: true,
  supportsOpens: true,
};

export const CHANNEL_OPTIONS: ChannelOption[] = [
  ALL_CHANNELS_OPTION,
  { value: 'email', label: 'Email', apiTypes: ['email'], supportsDelivered: true, supportsOpens: true },
  { value: 'push', label: 'Push', apiTypes: ['push', 'urban_airship'], supportsDelivered: true, supportsOpens: true },
  { value: 'in_app', label: 'In-app', apiTypes: ['in_app', 'inbox'], supportsDelivered: true, supportsOpens: true },
  { value: 'sms', label: 'SMS', apiTypes: ['twilio'], supportsDelivered: true, supportsOpens: false },
  { value: 'webhook', label: 'Webhook', apiTypes: ['webhook'], supportsDelivered: false, supportsOpens: false },
  { value: 'slack', label: 'Slack', apiTypes: ['slack'], supportsDelivered: true, supportsOpens: false },
  { value: 'line', label: 'LINE', apiTypes: ['line'], supportsDelivered: true, supportsOpens: false },
  { value: 'whatsapp', label: 'WhatsApp', apiTypes: ['whatsapp'], supportsDelivered: true, supportsOpens: false },
];

/** Returns whether the given channel option tracks delivery. */
export function channelSupportsDelivered(channel: ChannelOptionValue): boolean {
  return CHANNEL_OPTIONS.find((o) => o.value === channel)?.supportsDelivered !== false;
}

/** Returns whether the given channel option tracks opens and clicks. */
export function channelSupportsOpens(channel: ChannelOptionValue): boolean {
  return CHANNEL_OPTIONS.find((o) => o.value === channel)?.supportsOpens !== false;
}

/**
 * Returns the subset of CHANNEL_OPTIONS whose API types appear in the metrics
 * response. Only channels with matching metric data are returned — "All channels"
 * is excluded. Users only see channels enabled for their workspace.
 */
export function getEnabledChannelOptions(metrics: ServicesApiAllDeliveriesMetric[]): ChannelOption[] {
  const typesWithData = new Set(metrics.filter((m) => m.values.sent.some((v) => v > 0)).map((m) => m.type));
  return CHANNEL_OPTIONS.filter((o) => o.apiTypes != null && o.apiTypes.some((t) => typesWithData.has(t)));
}

/**
 * Normalize legacy timezone values stored in the DB to valid IANA timezones.
 * The Ember app has the same mapping — "Eastern" is a legacy value that
 * predates proper IANA timezone storage.
 */
export function normalizeTimezone(timezone: string): string {
  if (timezone === 'Eastern') return 'Etc/GMT+4';
  return timezone;
}

/**
 * Aggregates delivery metrics across all channels into daily totals.
 * When channelFilter is provided, only metrics whose type is in the filter list are summed.
 */
export function aggregateMetrics(
  metrics: ServicesApiAllDeliveriesMetric[],
  timezone?: string,
  channelFilter?: ServicesApiChannelType[] | null,
) {
  const filtered = channelFilter != null ? metrics.filter((m) => channelFilter.includes(m.type)) : metrics;

  if (filtered.length === 0) return { sent: [], delivered: [], opened: [], clicked: [], categories: [] };

  const length = filtered[0].values.sent.length;
  const sent = new Array<number>(length).fill(0);
  const delivered = new Array<number>(length).fill(0);
  const opened = new Array<number>(length).fill(0);
  const clicked = new Array<number>(length).fill(0);

  for (const metric of filtered) {
    for (let index = 0; index < length; index++) {
      sent[index] += metric.values.sent[index];
      delivered[index] += metric.values.delivered[index];
      opened[index] += metric.values.human_opened[index];
      clicked[index] += metric.values.human_clicked[index];
    }
  }

  const now = new Date();
  const dateFormatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (timezone != null) {
    dateFormatOptions.timeZone = timezone;
  }
  const categories: string[] = [];
  for (let index = length - 1; index >= 0; index--) {
    const date = new Date(now.getTime() - index * 86400 * 1000);
    categories.push(date.toLocaleDateString('en-US', dateFormatOptions));
  }

  return { sent, delivered, opened, clicked, categories };
}
